import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Calendar, ChevronRight, Loader2, PhoneOff, PhoneMissed, Send,
  ClipboardCheck, FileText, Award, XCircle, Users2, AlertTriangle, Zap, Printer,
  Sparkles, TrendingUp, Building2, Download, Clock
} from "lucide-react";
import api, { STATUS_LABELS } from "@/lib/api";
import { parseLocalIso } from "@/lib/dt";

const OVERALL_CARDS = [
  { key: "leads_recebidos",     label: "Leads recebidos",     icon: Users2,        color: "text-foreground" },
  { key: "tentativas_contacto", label: "Tentativas de contacto", icon: PhoneMissed, color: "text-[hsl(var(--warning))]" },
  { key: "nao_tratados",        label: "Ainda ninguém tratou",   icon: PhoneOff,    color: "text-[hsl(var(--danger))]" },
  { key: "estimativas_enviadas", label: "Estimativas enviadas", icon: Send,        color: "text-foreground" },
  { key: "qt_agendadas",        label: "QT agendadas",        icon: ClipboardCheck, color: "text-[#059669]" },
  { key: "propostas_enviadas",  label: "Propostas enviadas",  icon: FileText,      color: "text-foreground" },
  { key: "negocios_fechados",   label: "Negócios fechados",   icon: Award,         color: "text-[#059669]" },
  { key: "negocios_perdidos",   label: "Negócios perdidos",   icon: XCircle,       color: "text-[hsl(var(--danger))]" },
];

const ZOHO_BUCKET_LABELS = {
  junk: "Junk Lead",
  nao_contactado: "Not Contacted",
  tentado_contactar: "Attempted to Contact",
  contactado: "Contacted",
  qualificado: "Qualified",
  perdido: "Lost",
  convertido: "Converted / Won",
  outros: "Outros",
  sem_status_zoho: "Sem status Zoho",
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
};

export default function DailyReport() {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [reasonsEnum, setReasonsEnum] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null); // lead_id sendo guardado
  const [retryOpen, setRetryOpen] = useState(null); // lead_id com painel de retoma aberto
  const [retryDraft, setRetryDraft] = useState({ scheduled_at: "", note: "", also_zoho_task: true });
  const [retryLoading, setRetryLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const load = async (d = date) => {
    setLoading(true);
    try {
      const [rep, en] = await Promise.all([
        api.get(`/reports/daily`, { params: { date: d } }),
        reasonsEnum.length ? Promise.resolve({ data: { items: reasonsEnum } }) : api.get(`/reports/qi-pending-reasons`),
      ]);
      setData(rep.data);
      if (en?.data?.items) setReasonsEnum(en.data.items);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a carregar relatório");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(date); /* eslint-disable-next-line */ }, [date]);

  const setReason = async (leadId, key, note = null) => {
    setSaving(leadId);
    try {
      await api.post(`/leads/${leadId}/qi-pending-reason`, { reason_key: key, note });
      toast.success("Motivo guardado");
      await load(date);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setSaving(null); }
  };

  const openRetryAssistant = async (leadId) => {
    setRetryLoading(true);
    setRetryOpen(leadId);
    try {
      const r = await api.get(`/leads/${leadId}/retry-suggestion`);
      // datetime-local expects "YYYY-MM-DDTHH:mm"
      const iso = r.data.suggested_at || "";
      const local = iso ? iso.substring(0, 16) : "";
      setRetryDraft({ scheduled_at: local, note: r.data.rationale || "", also_zoho_task: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a obter sugestão");
      setRetryOpen(null);
    } finally { setRetryLoading(false); }
  };

  const confirmRetry = async (leadId) => {
    if (!retryDraft.scheduled_at) { toast.error("Escolhe data/hora"); return; }
    setRetryLoading(true);
    try {
      // 04/08/2026 — Store as naive local Portugal time. Appending "Z"
      // here made the backend treat the picker input as UTC and shifted
      // the follow-up +1h in Portugal summer (WEST=UTC+1).
      const isoWithSec = retryDraft.scheduled_at.length === 16
        ? `${retryDraft.scheduled_at}:00`
        : retryDraft.scheduled_at;
      const r = await api.post(`/leads/${leadId}/schedule-retry`, {
        scheduled_at: isoWithSec,
        also_zoho_task: retryDraft.also_zoho_task,
        note: retryDraft.note,
      });
      const zohoMsg = r.data.zoho?.attempted
        ? (r.data.zoho.ok ? " · Tarefa Zoho criada" : ` · Zoho falhou (${r.data.zoho.error || ""})`)
        : "";
      toast.success(`Retoma agendada${zohoMsg}`);
      setRetryOpen(null);
      await load(date);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setRetryLoading(false); }
  };

  const runAiAnalysis = async () => {
    setAiLoading(true);
    setAiOpen(true);
    try {
      const r = await api.post(`/reports/daily/ai-analysis`, {}, { params: { date } });
      setAi(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro na análise IA");
      setAiOpen(false);
    } finally { setAiLoading(false); }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const r = await api.get(`/reports/daily/pdf`, {
        params: { date, include_ai: !!ai },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Caring-Relatorio-Diario-${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Erro ao gerar PDF");
    } finally { setPdfLoading(false); }
  };

  const fmtNumber = (v) => {
    const f = Number(v || 0);
    return Number.isInteger(f) ? String(f) : f.toFixed(1);
  };
  const fmtEuro = (v) => `€ ${Math.round(Number(v || 0)).toLocaleString("pt-PT")}`;
  const metrics = data?.metrics_meta || [];
  const offices = data?.offices || [];
  const byOffice = data?.by_office || {};
  const byOperator = data?.by_operator_matrix || [];
  const rates = data?.conversion_rates || {};

  return (
    <div className="p-8 lg:p-12 max-w-[1600px] mx-auto space-y-8" data-testid="daily-report-root">
      {/* Print header (visible only on print) */}
      <div className="hidden print:block mb-4 pb-3 border-b">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Caring Estimativas · Relatório Diário</div>
        <div className="text-2xl font-serif">Relatório de {date}</div>
        <div className="text-[10px] text-muted-foreground mt-1">Gerado em {new Date().toLocaleString("pt-PT")}</div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 print:hidden">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Operacional</div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight text-foreground">Relatório diário</h1>
          <p className="text-sm text-muted-foreground mt-2">Visão diária do funil de clientes. Disponível para todos os utilizadores.</p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground/70" />
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground"
            data-testid="date-picker"
          />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground/70" />}
          <button
            onClick={downloadPdf}
            disabled={!data || loading || pdfLoading}
            className="inline-flex items-center gap-2 bg-card hover:bg-muted/40 border border-border text-foreground text-sm font-medium rounded-lg px-4 py-2 disabled:opacity-40"
            data-testid="btn-download-pdf"
            title="Descarregar em PDF (minimalista, A4)"
          >
            {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            PDF
          </button>
          <button
            onClick={() => window.print()}
            disabled={!data || loading}
            className="inline-flex items-center gap-2 bg-foreground hover:bg-[#1E293B] text-background text-sm font-medium rounded-lg px-4 py-2 disabled:opacity-40"
            data-testid="btn-print-report"
            title="Imprimir / Guardar em PDF"
          >
            <Printer className="w-4 h-4" /> Imprimir
          </button>
        </div>
      </div>

      {/* Overall KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {OVERALL_CARDS.map(c => {
          const Icon = c.icon;
          const val = data?.overall?.[c.key] ?? 0;
          return (
            <div key={c.key} data-testid={`kpi-${c.key}`} className="bg-card border border-border rounded-2xl p-5">
              <Icon className={`w-4 h-4 ${c.color} mb-3`} strokeWidth={1.5} />
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">{c.label}</div>
              <div className={`font-ui text-3xl tracking-tight ${c.color}`}>{val}</div>
            </div>
          );
        })}
      </div>

      {/* Conversion rates */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="conversion-rates">
        {[
          {k:"chamadas_para_estimativa", lbl:"Leads → Estimativa"},
          {k:"estimativa_para_rt",       lbl:"Estimativa → RT"},
          {k:"rt_para_negocio",          lbl:"RT → Negócio"},
          {k:"leads_para_negocio",       lbl:"Global (Leads → Negócio)"},
        ].map(x => (
          <div key={x.k} className="bg-card border border-border rounded-2xl p-5" data-testid={`rate-${x.k}`}>
            <TrendingUp className="w-4 h-4 text-foreground mb-3" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">{x.lbl}</div>
            <div className="font-ui text-3xl tracking-tight text-foreground">
              {rates[x.k] ?? 0}<span className="text-lg text-muted-foreground">%</span>
            </div>
          </div>
        ))}
      </div>

      {/* Timing stats — Tempo médio primeiro contacto + Chamada (18/02/2026) */}
      {data?.timing_stats && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="timing-stats">
          <div className="bg-card border border-border rounded-2xl p-5" data-testid="timing-first-contact">
            <Clock className="w-4 h-4 text-foreground mb-3" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">
              Tempo médio · 1º contacto
            </div>
            <div className="font-ui text-3xl tracking-tight text-foreground">
              {data.timing_stats.first_contact_avg_label || "—"}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              base: {data.timing_stats.first_contact_samples || 0} lead(s)
            </div>
          </div>
          <div className="bg-card border border-border rounded-2xl p-5" data-testid="timing-avg-call">
            <Clock className="w-4 h-4 text-foreground mb-3" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">
              Tempo médio em chamada
            </div>
            <div className="font-ui text-3xl tracking-tight text-foreground">
              {data.timing_stats.call_avg_label || "—"}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              base: {data.timing_stats.call_samples || 0} chamada(s)
            </div>
          </div>
          <div className="bg-card border border-border rounded-2xl p-5" data-testid="timing-total-call">
            <Clock className="w-4 h-4 text-foreground mb-3" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">
              Total em chamadas hoje
            </div>
            <div className="font-ui text-3xl tracking-tight text-foreground">
              {data.timing_stats.call_total_label || "—"}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              soma de todas as chamadas do dia
            </div>
          </div>
        </div>
      )}

      {/* Follow-ups manuais criados hoje (18/02/2026) — mostra quem
          é o colaborador do Atendimento associado ao caso para cada
          follow-up gerado pelo fluxo "Fim da chamada". A Direção usa
          esta secção para cruzar responsabilidades sem ter de abrir
          a página /followups. */}
      {(data?.manual_followups_today || []).length > 0 && (
        <div className="bg-card border border-border rounded-2xl p-6" data-testid="manual-followups-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--danger-bg))] border border-[hsl(var(--danger))]/25 flex items-center justify-center">
              <PhoneOff className="w-5 h-5 text-[hsl(var(--danger))]" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-display text-xl text-foreground">
                Follow-ups criados hoje
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-red-600 text-primary-foreground text-xs font-bold" data-testid="manual-followups-count">
                  {data.manual_followups_today.length}
                </span>
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Chamadas não atendidas ou reagendadas — cada linha identifica o colaborador do Atendimento com o caso.
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3">Lead</th>
                  <th className="py-2 pr-3">Atendimento (caso)</th>
                  <th className="py-2 pr-3">Motivo</th>
                  <th className="py-2 pr-3">Agendado</th>
                  <th className="py-2 pr-3">Criado por</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.manual_followups_today.map((fu) => (
                  <tr key={fu.id} data-testid={`manual-fu-row-${fu.id}`} className="border-b border-border/60 hover:bg-muted/40">
                    <td className="py-2 pr-3">
                      <a href={`/leads/${fu.lead_id}`} className="font-semibold text-foreground hover:text-foreground">
                        {fu.lead_name}
                      </a>
                      {fu.lead_phone && <div className="text-[11px] text-muted-foreground">{fu.lead_phone}</div>}
                    </td>
                    <td className="py-2 pr-3 text-[13px]">
                      <b>{fu.atendimento_owner_name || "—"}</b>
                      {fu.assigned_manager_name && <div className="text-[11px] text-muted-foreground">Gestor: {fu.assigned_manager_name}</div>}
                    </td>
                    <td className="py-2 pr-3 text-[13px]">
                      {fu.reason}
                      <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{fu.outcome}</div>
                    </td>
                    <td className="py-2 pr-3 text-[13px]">
                      {fu.scheduled_for && (() => {
                        const dt = parseLocalIso(fu.scheduled_for);
                        return dt ? dt.toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
                      })()}
                    </td>
                    <td className="py-2 pr-3 text-[13px] text-muted-foreground">
                      {fu.created_by_name || "—"}
                    </td>
                    <td className="py-2 pr-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                        fu.status === "done" ? "bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] border border-[hsl(var(--success))]/25" :
                        fu.status === "cancelled" ? "bg-muted text-muted-foreground border border-border" :
                        "bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))] border border-[hsl(var(--danger))]/25"
                      }`}>
                        {fu.status === "done" ? "concluído" : fu.status === "cancelled" ? "cancelado" : "pendente"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* AI Analysis panel */}
      <div className="bg-gradient-to-br from-[hsl(var(--warning-bg))] to-[hsl(var(--muted))] border border-[hsl(var(--warning))]/30 rounded-2xl p-6" data-testid="ai-panel">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-foreground" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-display text-xl text-foreground">Diagnóstico IA</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Análise executiva do dia com sugestões accionáveis.</p>
            </div>
          </div>
          {!aiOpen && (
            <button
              onClick={runAiAnalysis}
              disabled={!data || loading}
              data-testid="btn-run-ai-analysis"
              className="inline-flex items-center gap-2 bg-foreground hover:bg-slate-800 text-background text-sm font-medium rounded-full px-5 py-2.5 disabled:opacity-40"
            >
              <Sparkles className="w-4 h-4" /> Gerar análise
            </button>
          )}
        </div>
        {aiLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="ai-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> A gerar análise…
          </div>
        )}
        {ai && !aiLoading && (
          <div className="space-y-4" data-testid="ai-content">
            <p className="text-[15px] leading-relaxed text-foreground">{ai.diagnostic}</p>
            {ai.highlights?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#059669] font-semibold mb-2">Pontos altos</div>
                <ul className="space-y-1.5 text-sm text-foreground">
                  {ai.highlights.map((h,i) => <li key={i} className="flex gap-2"><span className="text-[#059669]">✓</span><span>{h}</span></li>)}
                </ul>
              </div>
            )}
            {ai.risks?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--danger))] font-semibold mb-2">Alertas</div>
                <ul className="space-y-1.5 text-sm text-foreground">
                  {ai.risks.map((h,i) => <li key={i} className="flex gap-2"><span className="text-[hsl(var(--danger))]">!</span><span>{h}</span></li>)}
                </ul>
              </div>
            )}
            {ai.suggestions?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold mb-2">Sugestões accionáveis</div>
                <ul className="space-y-1.5 text-sm text-foreground">
                  {ai.suggestions.map((h,i) => <li key={i} className="flex gap-2"><span className="text-foreground">→</span><span>{h}</span></li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Matrix — By Office */}
      <div className="bg-card border border-border rounded-2xl p-6" data-testid="matrix-by-office">
        <div className="flex items-center gap-3 mb-4">
          <Building2 className="w-5 h-5 text-foreground" strokeWidth={1.5} />
          <h2 className="font-display text-2xl">Resumo por Escritório</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="text-left py-2.5 px-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold border-b-2 border-primary">Métrica</th>
                {offices.map(o => (
                  <th key={o} className="text-right py-2.5 px-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold border-b-2 border-primary">{o}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {metrics.map((m, idx) => (
                <tr key={m.key} className={`${idx === metrics.length - 1 ? "font-semibold text-foreground border-t border-primary/30" : ""} ${idx % 2 === 1 ? "bg-muted/40" : ""}`}>
                  <td className="text-left py-2 px-3">{m.label}</td>
                  {offices.map(o => {
                    const val = (byOffice[o] || {})[m.key] ?? 0;
                    return (
                      <td key={o} className="text-right py-2 px-3 tabular-nums" data-testid={`office-${o}-${m.key}`}>
                        {m.key === "valor_total_eur" ? fmtEuro(val) : fmtNumber(val)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Matrix — By Operator */}
      <div className="bg-card border border-border rounded-2xl p-6" data-testid="matrix-by-operator">
        <div className="flex items-center gap-3 mb-4">
          <Users2 className="w-5 h-5 text-foreground" strokeWidth={1.5} />
          <h2 className="font-display text-2xl">Resumo por Operador</h2>
        </div>
        {byOperator.length === 0 ? (
          <div className="text-sm text-muted-foreground">Sem actividade hoje.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr>
                  <th className="text-left py-2.5 px-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold border-b-2 border-primary">Métrica</th>
                  {byOperator.slice(0, 6).map(op => (
                    <th key={op.id} className="text-right py-2.5 px-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold border-b-2 border-primary">{op.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {metrics.map((m, idx) => (
                  <tr key={m.key} className={`${idx === metrics.length - 1 ? "font-semibold text-foreground border-t border-primary/30" : ""} ${idx % 2 === 1 ? "bg-muted/40" : ""}`}>
                    <td className="text-left py-2 px-3">{m.label}</td>
                    {byOperator.slice(0, 6).map(op => (
                      <td key={op.id} className="text-right py-2 px-3 tabular-nums" data-testid={`op-${op.id}-${m.key}`}>
                        {m.key === "valor_total_eur" ? fmtEuro(op[m.key] || 0) : fmtNumber(op[m.key] || 0)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Per operator */}
      <div className="bg-card border border-border rounded-2xl p-8" data-testid="per-operator-section">
        <h2 className="font-display text-2xl mb-1">Por operador</h2>
        <p className="text-sm text-muted-foreground mb-5">Atendimento contabilizado nas leads recebidas / QI · Gestor de Cliente nas QT / propostas / fechos.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold border-b border-border">
                <th className="text-left py-2 px-3">Operador</th>
                <th className="text-left py-2 px-3">Papel</th>
                <th className="text-right py-2 px-3">Recebidos</th>
                <th className="text-right py-2 px-3">QI pendentes</th>
                <th className="text-right py-2 px-3">Estimativas</th>
                <th className="text-right py-2 px-3">QT agendadas</th>
                <th className="text-right py-2 px-3">Propostas</th>
                <th className="text-right py-2 px-3 text-[#059669]">Fechados</th>
                <th className="text-right py-2 px-3 text-[hsl(var(--danger))]">Perdidos</th>
              </tr>
            </thead>
            <tbody>
              {(data?.per_operator || []).length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-muted-foreground text-xs">Sem atividade neste dia.</td></tr>
              )}
              {(data?.per_operator || []).map(r => (
                <tr key={r.id + r.role} className="border-b border-border/60" data-testid={`op-row-${r.id}`}>
                  <td className="py-2 px-3 font-medium">{r.name}</td>
                  <td className="py-2 px-3">
                    <span className={`inline-flex text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border ${r.role === "gestor" ? "bg-primary/15 text-foreground border-primary/40" : "bg-[hsl(var(--success-bg))] text-[#059669] border-[hsl(var(--success))]/40"}`}>{r.role}</span>
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">{r.leads_recebidos}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{r.qi_nao_contactadas}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{r.estimativas_enviadas}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{r.qt_agendadas}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{r.propostas_enviadas}</td>
                  <td className="py-2 px-3 text-right tabular-nums font-semibold text-[#059669]">{r.negocios_fechados}</td>
                  <td className="py-2 px-3 text-right tabular-nums font-semibold text-[hsl(var(--danger))]">{r.negocios_perdidos}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pending — with reason capture */}
      <div className="bg-card border border-border rounded-2xl p-8" data-testid="pending-section">
        <div className="flex items-center gap-3 mb-4">
          <PhoneOff className="w-5 h-5 text-[hsl(var(--danger))]" />
          <h2 className="font-display text-2xl">Leads recebidas mas ainda não contactadas</h2>
        </div>
        {(data?.motives_pending || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="motives-summary">
            {data.motives_pending.map(m => (
              <span key={m.key} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border bg-muted/40 border-border text-muted-foreground">
                {m.label}: <span className="font-semibold tabular-nums">{m.count}</span>
              </span>
            ))}
          </div>
        )}
        {(data?.pending_leads || []).length === 0 ? (
          <div className="text-sm text-muted-foreground">Nenhuma lead por contactar neste dia. ✨</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold border-b border-border">
                  <th className="text-left py-2 px-3">Lead</th>
                  <th className="text-left py-2 px-3">Recebido</th>
                  <th className="text-left py-2 px-3">Atendimento</th>
                  <th className="text-left py-2 px-3">Estado</th>
                  <th className="text-left py-2 px-3">Motivo</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {data.pending_leads.map(l => (
                  <tr key={l.id} className="border-b border-border/60 relative" data-testid={`pending-row-${l.id}`}>
                    <td className="py-2 px-3">
                      <div className="font-medium">{l.name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{l.phone || "—"} · {l.email || "—"}</div>
                    </td>
                    <td className="py-2 px-3 text-xs text-muted-foreground tabular-nums">{(l.created_at || "").substring(11,16)}</td>
                    <td className="py-2 px-3 text-xs">{l.operator?.name || <span className="text-muted-foreground/70">—</span>}</td>
                    <td className="py-2 px-3">
                      {l.treated ? (
                        <span
                          data-testid={`pending-treated-${l.id}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))] font-semibold"
                          title="Já houve pelo menos uma tentativa de contacto"
                        >
                          <PhoneMissed className="w-3 h-3" /> tentativa
                        </span>
                      ) : (
                        <span
                          data-testid={`pending-untouched-${l.id}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[hsl(var(--danger-bg))] border border-[hsl(var(--danger))]/40 text-[hsl(var(--danger))] font-semibold"
                          title="Ninguém tocou neste lead ainda"
                        >
                          <PhoneOff className="w-3 h-3" /> não tratado
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <select
                        data-testid={`reason-select-${l.id}`}
                        value={l.qi_pending_reason || ""}
                        onChange={e => setReason(l.id, e.target.value)}
                        disabled={saving === l.id}
                        className="bg-muted/40 border border-border rounded-lg px-2 py-1.5 text-xs text-foreground w-full max-w-[240px] disabled:opacity-60"
                      >
                        <option value="" disabled>— escolher motivo —</option>
                        {reasonsEnum.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                      </select>
                      {l.qi_pending_reason_note && <div className="text-[10px] text-muted-foreground mt-1 italic">{l.qi_pending_reason_note}</div>}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        {l.qi_pending_reason === "aguarda_retorno" && (
                          <button
                            onClick={() => openRetryAssistant(l.id)}
                            disabled={retryLoading && retryOpen === l.id}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full border border-primary/60 bg-muted/40 text-foreground hover:bg-muted font-semibold disabled:opacity-60"
                            data-testid={`retry-btn-${l.id}`}
                            title="Assistente de Retoma"
                          >
                            <Zap className="w-3 h-3" /> Retomar
                          </button>
                        )}
                        <Link to={`/leads/${l.id}`} className="text-foreground hover:text-foreground"><ChevronRight className="w-4 h-4" /></Link>
                      </div>
                      {retryOpen === l.id && (
                        <div className="absolute right-8 mt-2 z-20 bg-card border border-border rounded-xl shadow-lg p-4 w-80 space-y-3 print:hidden" data-testid={`retry-panel-${l.id}`}>
                          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold flex items-center gap-2">
                            <Zap className="w-3 h-3 text-foreground" /> Assistente de Retoma
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Data/hora sugerida</div>
                            <input
                              type="datetime-local"
                              value={retryDraft.scheduled_at}
                              onChange={e => setRetryDraft(v => ({ ...v, scheduled_at: e.target.value }))}
                              className="w-full bg-muted/40 border border-border rounded-lg px-2 py-1.5 text-xs"
                              data-testid={`retry-datetime-${l.id}`}
                            />
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Racional / Nota</div>
                            <textarea
                              rows={3}
                              value={retryDraft.note}
                              onChange={e => setRetryDraft(v => ({ ...v, note: e.target.value }))}
                              className="w-full bg-muted/40 border border-border rounded-lg px-2 py-1.5 text-xs"
                              data-testid={`retry-note-${l.id}`}
                            />
                          </div>
                          <label className="flex items-center gap-2 text-xs cursor-pointer">
                            <input type="checkbox" checked={retryDraft.also_zoho_task} onChange={e => setRetryDraft(v => ({ ...v, also_zoho_task: e.target.checked }))} data-testid={`retry-zoho-${l.id}`} />
                            <span>Criar tarefa no Zoho CRM</span>
                          </label>
                          <div className="flex justify-end gap-2 pt-1">
                            <button onClick={() => setRetryOpen(null)} className="text-xs px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:bg-muted">Cancelar</button>
                            <button
                              onClick={() => confirmRetry(l.id)}
                              disabled={retryLoading}
                              className="text-xs px-3 py-1.5 rounded-full bg-foreground hover:bg-slate-800 text-background font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                              data-testid={`retry-confirm-${l.id}`}
                            >
                              {retryLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Calendar className="w-3 h-3" />} Agendar
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Lost — reasons */}
      <div className="bg-card border border-border rounded-2xl p-8" data-testid="lost-section">
        <div className="flex items-center gap-3 mb-4">
          <XCircle className="w-5 h-5 text-[hsl(var(--danger))]" />
          <h2 className="font-display text-2xl">Negócios perdidos — motivos</h2>
        </div>
        {(data?.motives_lost || []).length === 0 ? (
          <div className="text-sm text-muted-foreground">Sem perdas registadas neste dia.</div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              {data.motives_lost.map(m => (
                <span key={m.reason} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border bg-[hsl(var(--danger-bg))] border-[#F5CACA] text-[hsl(var(--danger))]">
                  {m.reason}: <span className="font-semibold tabular-nums">{m.count}</span>
                </span>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold border-b border-border">
                    <th className="text-left py-2 px-3">Lead</th>
                    <th className="text-left py-2 px-3">Estado</th>
                    <th className="text-left py-2 px-3">Motivo</th>
                    <th className="text-left py-2 px-3">Operador</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.lost_leads || []).map(l => (
                    <tr key={l.id} className="border-b border-border/60" data-testid={`lost-row-${l.id}`}>
                      <td className="py-2 px-3 font-medium">
                        <Link to={`/leads/${l.id}`} className="hover:underline">{l.name || "—"}</Link>
                      </td>
                      <td className="py-2 px-3 text-xs">{STATUS_LABELS[l.status] || l.status}</td>
                      <td className="py-2 px-3 text-xs text-muted-foreground">{l.loss_reason || "—"}</td>
                      <td className="py-2 px-3 text-xs">{l.operator?.name || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Zoho Lead_Status buckets */}
      {(data?.zoho_buckets || []).length > 0 && (
        <div className="bg-card border border-border rounded-2xl p-8" data-testid="zoho-buckets-section">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-foreground" />
            <h2 className="font-display text-2xl">Status Zoho das leads deste dia</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.zoho_buckets.map(b => (
              <span key={b.bucket} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border bg-muted/40 border-border text-muted-foreground">
                {ZOHO_BUCKET_LABELS[b.bucket] || b.bucket}: <span className="font-semibold tabular-nums">{b.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
